/**
 * The pre-submit verdict: asks how this feedback is likely to be judged while
 * the person is still typing.
 *
 * ADVISORY, NEVER A GATE. Nothing here may prevent a submission. Every failure
 * path resolves to null so the caller renders no badge: not configured, too
 * short, unauthorized, rate limited, timed out, upstream failure, malformed
 * response, and the BFF's `204 No Content`.
 *
 * Same-origin (`/api/feedback/evaluate`). No Authorization header and no email
 * in the body: the BFF takes identity from the session cookie. Redaction is
 * applied here for the same reason `submit.ts` applies it.
 */
import { CSRF_HEADER } from '@/api/client';
import { endpoints } from './config';
import { withBase } from '@/lib/basePath';
import { redact, redactRoute } from './redact';
import type { FeedbackTool } from './types';

export type Verdict = 'good' | 'neutral' | 'bad';

export interface VerdictResult {
  verdict: Verdict;
  sentence: string;
}

const VERDICTS: readonly string[] = ['good', 'neutral', 'bad'];

/** Below this there is nothing to judge. Matches the BFF and leon-intake. */
export const MIN_FEEDBACK_CHARS = 12;

export interface EvaluateInput {
  tool: FeedbackTool;
  feedback: string;
  route: string;
  csrfToken: string | null;
  /** Aborted when the person types again, so a stale verdict cannot land. */
  signal?: AbortSignal;
}

/** Resolves null for every "no opinion" case; rejects only on AbortError. */
export async function evaluateFeedback(input: EvaluateInput): Promise<VerdictResult | null> {
  const feedback = redact(input.feedback).trim();
  if (feedback.length < MIN_FEEDBACK_CHARS) return null;

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (input.csrfToken) headers[CSRF_HEADER] = input.csrfToken;

  let response: Response;
  try {
    response = await fetch(withBase(`${endpoints.feedback}/evaluate`), {
      method: 'POST',
      headers,
      body: JSON.stringify({ tool: input.tool, feedback, route: redactRoute(input.route) }),
      credentials: 'same-origin',
      signal: input.signal,
    });
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') throw err;
    return null;
  }

  if (!response.ok) return null;

  const data = (await response.json().catch(() => null)) as Partial<VerdictResult> | null;
  if (!data || typeof data.verdict !== 'string' || !VERDICTS.includes(data.verdict)) return null;
  if (typeof data.sentence !== 'string' || data.sentence.trim().length === 0) return null;

  return { verdict: data.verdict as Verdict, sentence: data.sentence.trim() };
}
